/**
 * Server-side Guardian conversation history (audit P-12, 2026-10-22).
 *
 * Replaces device-local chat history with the agent's own record, read with the
 * user's Guardian bearer (the same credential as the WebSocket). The history
 * lives on the AGENT HTTP origin — derived from the WebSocket URL the app
 * already uses — not the Backend API.
 *
 * Contract (plan docs/guardian/06 "History API"):
 *   GET /v1/guardian/conversations?limit=&cursor=
 *     -> { items:[{conversationId,title,updatedAt,messageCount}], nextCursor }
 *   GET /v1/guardian/conversations/{id}
 *     -> { conversationId, messages:[{role:"user"|"assistant", text, at}] }
 *
 * All responses are hostile until parsed: shapes are validated, counts and text
 * length are bounded, and `text` is carried as text only (callers render it as
 * text, never HTML). An older agent that lacks the endpoint answers 404/503;
 * that and any network failure are reported as `supported:false` so the caller
 * can fall back to the current local behaviour WITHOUT surfacing an error.
 */

const DEFAULT_WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:9000/ws';

const MAX_ITEMS = 200;
const MAX_MESSAGES = 200;
const MAX_TEXT = 100000;
const MAX_TITLE = 200;
const REQUEST_TIMEOUT_MS = 15000;

/**
 * The agent HTTP origin for history, derived from the WebSocket URL (wss->https,
 * ws->http), path/query stripped. Mirrors the voice helper so one config drives
 * every agent call.
 * @param {string} [wsUrl]
 * @returns {string}
 */
export function agentHttpBase(wsUrl = DEFAULT_WS_URL) {
  try {
    const u = new URL(wsUrl);
    u.protocol = u.protocol === 'wss:' ? 'https:' : 'http:';
    u.pathname = '';
    u.search = '';
    u.hash = '';
    return u.toString().replace(/\/$/, '');
  } catch {
    return process.env.REACT_APP_AGENT_HTTP_URL || 'http://localhost:9000';
  }
}

function boundedString(value, max) {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

/** A timestamp is kept only when it is a string or finite number; else null. */
function boundedTimestamp(value) {
  if (typeof value === 'string') return value.slice(0, 64);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function sanitizeListItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const conversationId = typeof raw.conversationId === 'string' ? raw.conversationId : null;
  if (!conversationId) return null;
  const count = Number(raw.messageCount);
  return {
    conversationId,
    title: boundedString(raw.title, MAX_TITLE),
    updatedAt: boundedTimestamp(raw.updatedAt),
    messageCount: Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0,
  };
}

function sanitizeMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.role !== 'user' && raw.role !== 'assistant') return null;
  if (typeof raw.text !== 'string') return null;
  return {
    role: raw.role,
    text: raw.text.slice(0, MAX_TEXT),
    at: boundedTimestamp(raw.at),
  };
}

async function readJson(res) {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function historyFetch(path, token, { wsUrl } = {}) {
  if (!token) {
    return { ok: false, supported: true, status: 0, reason: 'no-token' };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${agentHttpBase(wsUrl)}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    // 404 = endpoint absent (older agent); 503 = temporarily unavailable.
    // Both mean "fall back to local history without an error".
    if (res.status === 404 || res.status === 503) {
      return { ok: false, supported: false, status: res.status, reason: 'unsupported' };
    }
    const body = await readJson(res);
    if (!res.ok || !body || typeof body !== 'object') {
      return { ok: false, supported: true, status: res.status, reason: 'bad-response' };
    }
    return { ok: true, supported: true, status: res.status, body };
  } catch (err) {
    // Timeout/offline: do not error the UI — fall back to local history.
    return { ok: false, supported: false, status: 0, reason: err?.name === 'AbortError' ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Lists the caller's own conversations (newest first per the agent).
 * @param {string} token - Guardian bearer.
 * @param {{ limit?: number, cursor?: string, wsUrl?: string }} [opts]
 * @returns {Promise<{supported:boolean, items:Array, nextCursor:(string|null), status:number}>}
 */
export async function fetchGuardianConversations(token, opts = {}) {
  const limit = Math.min(50, Math.max(1, Math.floor(Number(opts.limit)) || 20));
  const params = new URLSearchParams({ limit: String(limit) });
  if (typeof opts.cursor === 'string' && opts.cursor) params.set('cursor', opts.cursor);

  const res = await historyFetch(`/v1/guardian/conversations?${params.toString()}`, token, opts);
  if (!res.ok) {
    return { supported: res.supported, items: [], nextCursor: null, status: res.status };
  }
  const rawItems = Array.isArray(res.body.items) ? res.body.items : [];
  const items = rawItems
    .slice(0, MAX_ITEMS)
    .map(sanitizeListItem)
    .filter(Boolean);
  const nextCursor = typeof res.body.nextCursor === 'string' && res.body.nextCursor
    ? res.body.nextCursor
    : null;
  return { supported: true, items, nextCursor, status: res.status };
}

/**
 * Reads one conversation's messages (text only; tool payloads are omitted by
 * the agent). Another user's id answers 404 -> treated as not found.
 * @param {string} token - Guardian bearer.
 * @param {string} id - Conversation id.
 * @param {{ wsUrl?: string }} [opts]
 * @returns {Promise<{supported:boolean, found:boolean, conversationId:(string|null), messages:Array, status:number}>}
 */
export async function fetchGuardianConversation(token, id, opts = {}) {
  if (typeof id !== 'string' || !id) {
    return { supported: true, found: false, conversationId: null, messages: [], status: 0 };
  }
  const res = await historyFetch(`/v1/guardian/conversations/${encodeURIComponent(id)}`, token, opts);
  if (!res.ok) {
    // 404 here means "not the caller's / gone": not found, but the API exists
    // when supported !== false for other reasons.
    const found = false;
    return { supported: res.supported, found, conversationId: null, messages: [], status: res.status };
  }
  const conversationId = typeof res.body.conversationId === 'string' ? res.body.conversationId : id;
  const rawMessages = Array.isArray(res.body.messages) ? res.body.messages : [];
  const messages = rawMessages
    .slice(0, MAX_MESSAGES)
    .map(sanitizeMessage)
    .filter(Boolean);
  return { supported: true, found: true, conversationId, messages, status: res.status };
}
